import {
	Autocomplete,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Menu,
	MenuItem,
	TextField,
	Tooltip,
	Typography,
	ButtonBase,
} from '@mui/material';
import React, { useCallback } from 'react';
import { AddEvent } from '../../blocks/icons/AddEvent';
import { Send } from '../../blocks/icons/send';
import MuiModal from '../../mui/MuiModal/MuiModal';
import MuiTextArea from '../../mui/MuiTextArea/MuiTextArea';
import { CalendarSourceType } from '#types';
import styles from './CalendarLinks.module.scss';
import { useCalendarLinks } from './useCalendarLinks';

export { CalendarSourceType } from '#types';

export type CalendarLinksProps = {
	sourceType: CalendarSourceType;
	sourceId: string;
	iconOnly?: boolean;
	canSendCalendarInvite?: boolean;
	customerEmail?: string;
	/** e.g. close a parent popover when a menu destination is chosen (menu is portaled) */
	onCalendarMenuItemSelect?: () => void;
};

export const CalendarLinks: React.FC<CalendarLinksProps> = ( {
	sourceType,
	sourceId,
	iconOnly = false,
	canSendCalendarInvite = false,
	customerEmail,
	onCalendarMenuItemSelect,
} ) => {
	const {
		anchorEl,
		isMenuOpen,
		linksLoading,
		sendLoading,
		links,
		intro,
		setIntro,
		recipientEmails,
		handleRecipientEmailsChange,
		handleRecipientEmailInputBlur,
		isInviteModalOpen,
		handleMenuOpen,
		handleMenuClose,
		handleInviteMenuItemClick,
		handleInviteModalClose,
		handleSendInvite,
	} = useCalendarLinks( {
		sourceType,
		sourceId,
		customerEmail,
	} );

	const handleDestinationMenuItemClick = useCallback( () => {
		handleMenuClose();
		onCalendarMenuItemSelect?.();
	}, [ handleMenuClose, onCalendarMenuItemSelect ] );

	const button = (
		<ButtonBase
			component='button'
			onClick={ handleMenuOpen }
			className={ styles.button }
			data-cy='add-to-calendar-button'
			aria-label='Add to calendar'
			aria-haspopup='true'
			aria-expanded={ isMenuOpen || undefined }
			type='button'
		>
			{ linksLoading ? (
				<CircularProgress
					size={ 14 }
					className={ styles.spinner }
					aria-hidden='true'
				/>
			) : (
				<AddEvent className={ styles.icon } aria-hidden='true' />
			) }
			{ !iconOnly && (
				<Typography className={ styles.buttonText }>Add to calendar</Typography>
			) }
		</ButtonBase>
	);

	return (
		<Box className={ styles.container }>
			{ iconOnly ? <Tooltip title='Add to calendar'>{ button }</Tooltip> : button }
			<Menu
				anchorEl={ anchorEl }
				open={ isMenuOpen && !linksLoading }
				onClose={ handleMenuClose }
				data-cy='calendar-links-menu'
				slotProps={ {
					list: { 'aria-label': 'calendar link options' },
				} }
			>
				<MenuItem
					component='a'
					href={ links?.googleUrl }
					target='_blank'
					rel='noreferrer'
					onClick={ handleDestinationMenuItemClick }
					data-cy='google-calendar-link'
				>
					+Google Calendar
				</MenuItem>
				<MenuItem
					component='a'
					href={ links?.outlookUrl }
					target='_blank'
					rel='noreferrer'
					onClick={ handleDestinationMenuItemClick }
					data-cy='outlook-calendar-link'
				>
					+Outlook Calendar
				</MenuItem>
				<MenuItem
					component='a'
					href={ links?.webcalUrl }
					target='_blank'
					rel='noreferrer'
					onClick={ handleDestinationMenuItemClick }
					data-cy='apple-calendar-link'
				>
					+Desktop Calendar
				</MenuItem>
				<MenuItem
					component='a'
					href={ links?.downloadUrl }
					target='_blank'
					rel='noreferrer'
					onClick={ handleDestinationMenuItemClick }
					data-cy='download-ics-link'
				>
					Download .ics file
				</MenuItem>
				{ canSendCalendarInvite ? (
					<>
						<Divider />
						<MenuItem
							onClick={ () => {
								handleInviteMenuItemClick();
								onCalendarMenuItemSelect?.();
							} }
							data-cy='send-calendar-invite-link'
						>
							Send calendar reminder
						</MenuItem>
					</>
				) : null }
			</Menu>
			<MuiModal
				muiModalProps={ {
					open: isInviteModalOpen,
					onClose: handleInviteModalClose,
					slotProps: { paper: { 'data-cy': 'send-calendar-invite-modal' } },
				} }
				title='Send calendar reminder'
				controls={
					<>
						<Button
							variant='outlined'
							onClick={ handleInviteModalClose }
							disabled={ sendLoading }
							data-cy='cancel-button'
						>
							Cancel
						</Button>
						<Button
							startIcon={ <Send className={ styles.sendButtonIcon } /> }
							onClick={ handleSendInvite }
							disabled={ sendLoading || recipientEmails.length === 0 }
							data-cy='confirm-send-button'
						>
							{ sendLoading ? 'Sending…' : 'Send' }
						</Button>
					</>
				}
			>
				<Box className={ styles.messageContainer }>
					<Box className={ styles.recipientsSection }>
						<Typography component='label' className={ styles.recipientLabel }>
							Recipients
						</Typography>
						<Autocomplete
							freeSolo
							multiple
							clearOnBlur
							options={ [] }
							value={ recipientEmails }
							onChange={ ( _, newValue ) => handleRecipientEmailsChange( newValue ) }
							renderInput={ ( params ) => (
								<TextField
									{ ...params }
									variant='standard'
									placeholder={
										recipientEmails.length === 0 ? 'Add email addresses' : ''
									}
									className={ styles.recipientInput }
									slotProps={ {
										...params.slotProps,
										htmlInput: {
											...params.slotProps.htmlInput,
											'aria-label': 'Add recipient email address',
											'data-cy': 'recipient-email-input',
										},
									} }
									onBlur={ handleRecipientEmailInputBlur }
								/>
							) }
							renderValue={ ( selectedValues, getItemProps ) =>
								selectedValues.map( ( email, index ) => {
									const { key, ...itemProps } = getItemProps( { index } );
									return (
										<Chip
											key={ key }
											label={ email }
											{ ...itemProps }
											data-cy={ `recipient-${ email }` }
										/>
									);
								} )
							}
						/>
					</Box>
					<MuiTextArea
						label='Opening message'
						value={ intro }
						onChange={ ( e ) => setIntro( e.target.value ) }
						maxLength={ 500 }
						countCharacters
						multiline
						minRows={ 4 }
						className={ styles.textarea }
					/>
					<Box className={ styles.fixedSection }>
						<Typography className={ styles.fixedSectionLabel }>
							Calendar links (not editable)
						</Typography>
						<Typography className={ styles.fixedSectionContent }>
							Add to Google Calendar · Add to Outlook · Download .ics file
						</Typography>
					</Box>
				</Box>
			</MuiModal>
		</Box>
	);
};
